@if (error(); as e) { <div class="alert alert-warning">{{ ('companion.errors.' + e) | translate }}</div> }

@if (metrics(); as m) {
  <div class="cp-card">
    <h2 class="cp-h">{{ 'companion.diary.metrics_title' | translate }}</h2>
    <p class="cp-hint">{{ 'companion.diary.metrics_hint' | translate }}</p>
    <div class="metrics">
      <div class="metric"><b>{{ kg(m.current_weight_kg) }}</b><span>{{ 'companion.diary.metrics.current_weight' | translate }}</span></div>
      <div class="metric"><b>{{ kg(m.start_weight_kg) }}</b><span>{{ 'companion.diary.metrics.start_weight' | translate }}</span></div>
      <div class="metric"><b>{{ signed(m.weight_change_30d_kg) }}</b><span>{{ 'companion.diary.metrics.change_30d' | translate }}</span></div>
      <div class="metric"><b>{{ signed(m.weight_change_90d_kg) }}</b><span>{{ 'companion.diary.metrics.change_90d' | translate }}</span></div>
      <div class="metric"><b>{{ m.sleep_hours_avg_30d ?? '—' }} h</b><span>{{ 'companion.diary.metrics.sleep_30d' | translate }}</span></div>
      <div class="metric"><b>{{ m.blood_pressure_avg_30d ? (m.blood_pressure_avg_30d.systolic + '/' + m.blood_pressure_avg_30d.diastolic) : '—' }}</b><span>{{ 'companion.diary.metrics.bp_30d' | translate }}</span></div>
      <div class="metric"><b>{{ m.heart_rate_avg_30d ?? '—' }}</b><span>{{ 'companion.diary.metrics.pulse_30d' | translate }}</span></div>
      <div class="metric"><b>{{ m.exercise_days_per_week.join(' · ') }}</b><span>{{ 'companion.diary.metrics.exercise_weeks' | translate }}</span></div>
      <div class="metric"><b>{{ m.checkin_days_30d }}</b><span>{{ 'companion.diary.metrics.checkin_days' | translate }}</span></div>
      <div class="metric"><b>{{ m.confirmed_plans }}</b><span>{{ 'companion.diary.metrics.plans' | translate }}</span></div>
      @if (m.bmi) { <div class="metric"><b>{{ m.bmi }}</b><span>{{ 'companion.diary.metrics.bmi' | translate }}</span></div> }
    </div>
    <p class="cp-hint mt-2 mb-0">{{ 'companion.diary.no_reference_note' | translate }}</p>
  </div>
}

@if (loading() && !days().length) {
  <div class="text-center py-3"><c-spinner /></div>
} @else if (!days().length) {
  <div class="cp-card"><p class="mb-0 cp-hint">{{ 'companion.diary.empty' | translate }}</p></div>
}

@for (day of days(); track day.date) {
  <div class="cp-card">
    <div class="day__h">
      <h2 class="cp-h mb-0">{{ day.date | date:'fullDate':undefined:lang() }}</h2>
      @if (day.plan) { <span class="cp-chip cp-chip--ok">{{ 'companion.diary.plan_agreed' | translate }}</span> }
    </div>

    @for (o of current(day); track o.id) {
      <div class="obs" [class.obs--unclear]="o.state === 'unclear'">
        <div>
          <div class="fw-semibold">{{ ('companion.kinds.' + o.kind) | translate }}</div>
          @if (o.measured_at) { <div class="cp-hint">{{ o.measured_at | date:'shortTime':undefined:lang() }}</div> }
          @if (o.details['context']) { <div class="cp-hint">{{ ('companion.form.contexts.' + o.details['context']) | translate }}</div> }
        </div>
        <div>
          @if (editing() === o.id) {
            <div class="edit">
              @if (o.kind === 'blood_pressure') {
                <input class="form-control form-control-sm" type="number" [ngModel]="editValue()" (ngModelChange)="editValue.set($event)" /> /
                <input class="form-control form-control-sm" type="number" [ngModel]="editValue2()" (ngModelChange)="editValue2.set($event)" />
              } @else if (isNumeric(o.kind)) {
                <input class="form-control form-control-sm" type="number" step="0.1" [ngModel]="editValue()" (ngModelChange)="editValue.set($event)" />
              } @else {
                <input class="form-control form-control-sm" type="text" [ngModel]="editText()" (ngModelChange)="editText.set($event)" />
              }
              <button cButton color="primary" size="sm" (click)="saveEdit(o)">{{ 'companion.diary.save_correction' | translate }}</button>
              <button cButton color="secondary" variant="ghost" size="sm" (click)="markSkipped(o)">{{ 'companion.diary.mark_skipped' | translate }}</button>
              <button cButton color="secondary" variant="ghost" size="sm" (click)="editing.set(null)">{{ 'companion.cancel' | translate }}</button>
            </div>
          } @else {
            <span>{{ formatValue(o) }}</span>
            @if (o.state === 'unclear' && o.clarification) { <div class="cp-hint">{{ o.clarification }}</div> }
            @if (o.details['amount']) { <div class="cp-hint">{{ o.details['amount'] }} {{ o.details['unit'] || '' }}</div> }
            @if (o.details['duration_min']) { <div class="cp-hint">{{ o.details['duration_min'] }} min</div> }
          }
        </div>
        <div>
          @if (!readOnly() && editing() !== o.id && o.kind !== 'plan_review') {
            <button type="button" class="btn btn-sm btn-link" (click)="startEdit(o)">{{ 'companion.diary.correct' | translate }}</button>
          }
        </div>
      </div>
    }

    @if (old(day).length) {
      <button type="button" class="btn btn-sm btn-link px-0" (click)="toggleOld(day.date)">
        {{ (showOld().has(day.date) ? 'companion.diary.hide_corrections' : 'companion.diary.show_corrections') | translate:{ count: old(day).length } }}
      </button>
      @if (showOld().has(day.date)) {
        @for (o of old(day); track o.id) {
          <div class="obs obs--old">
            <div>{{ ('companion.kinds.' + o.kind) | translate }}</div>
            <div>{{ formatValue(o) }}</div>
            <div class="cp-hint">{{ o.created_at | date:'short':undefined:lang() }}</div>
          </div>
        }
      }
    }

    @if (day.plan; as plan) {
      <div class="mt-2">
        <div class="fw-semibold">{{ 'companion.diary.plan_title' | translate }}</div>
        @if (plan.summary) { <div>{{ plan.summary }}</div> }
        <ul class="cp-steps">
          @for (s of plan.steps; track $index; let i = $index) {
            <li>
              <span>{{ plan.review?.done?.includes(i) ? '✓' : '○' }}</span>
              <span class="cp-area">{{ ('companion.plan.areas.' + s.area) | translate }}</span>
              <span>{{ s.text }}</span>
            </li>
          }
        </ul>
        @if (plan.review?.notes) { <div class="cp-hint">{{ plan.review?.notes }}</div> }
      </div>
    }
  </div>
}

@if (days().length) {
  <div class="text-center">
    <button cButton color="secondary" variant="outline" [disabled]="loading()" (click)="earlier()">{{ 'companion.diary.earlier' | translate }}</button>
  </div>
}
